'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Heart, Trash2, Play, MessageCircle, Send, ChevronDown } from 'lucide-react';
import { FeedPost, FeedComment, ConfessionMood, api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';

interface PostCardProps {
  post: FeedPost;
  onDelete?: (id: string) => void;
  onLikeChange?: (id: string, likedByMe: boolean, likeCount: number) => void;
  onCommentAdded?: (id: string, newCount: number) => void;
}

// ── Anonymous avatar — color derived from user id ─────────────────────────
const AVATAR_COLORS = [
  'bg-violet-500/20 text-violet-400 border-violet-500/30',
  'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'bg-red-500/20 text-red-400 border-red-500/30',
  'bg-blue-500/20 text-blue-400 border-blue-500/30',
];

function avatarColor(userId: string) {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function avatarEmoji(userId: string, gender?: string | null) {
  if (gender === 'FEMALE') return '🌸';
  if (gender === 'MALE') return '👤';
  const emojis = ['🎭', '🌙', '⚡', '🦋', '🔥', '🌊', '🎲', '👻'];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return emojis[hash % emojis.length];
}

// ── Confession mood config ────────────────────────────────────────────────
const MOOD_CONFIG: Record<ConfessionMood, { emoji: string; label: string; bg: string; border: string; text: string }> = {
  FUNNY:     { emoji: '😂', label: 'Funny',     bg: 'bg-yellow-500/10',  border: 'border-yellow-500/25', text: 'text-yellow-300' },
  SPICY:     { emoji: '🌶️', label: 'Spicy',    bg: 'bg-red-500/10',     border: 'border-red-500/25',    text: 'text-red-400'    },
  SAD:       { emoji: '😢', label: 'Sad',       bg: 'bg-blue-500/10',    border: 'border-blue-500/25',   text: 'text-blue-400'   },
  WHOLESOME: { emoji: '🥰', label: 'Wholesome', bg: 'bg-pink-500/10',    border: 'border-pink-500/25',   text: 'text-pink-400'   },
  RANDOM:    { emoji: '🎲', label: 'Random',    bg: 'bg-purple-500/10',  border: 'border-purple-500/25', text: 'text-purple-400' },
};

function timeAgo(date: string) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function getYouTubeId(url: string) {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^&?\s]{11})/);
  return m ? m[1] : null;
}

// ── PostCard ──────────────────────────────────────────────────────────────
export function PostCard({ post, onDelete, onLikeChange, onCommentAdded }: PostCardProps) {
  const { token, user } = useAuthStore();
  const { addToast } = useUIStore();
  const [liking, setLiking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [imgExpanded, setImgExpanded] = useState(false);

  // Comments state
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState<FeedComment[]>([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentCount, setCommentCount] = useState(post.commentCount ?? 0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function loadComments(cursor?: string) {
    setCommentsLoading(true);
    try {
      const res = await api.feed.getComments(post.id, cursor);
      setComments((prev) => cursor ? [...prev, ...res.comments] : res.comments);
      setNextCursor(res.nextCursor);
      setHasMore(res.hasMore);
      setCommentsLoaded(true);
    } catch {
      addToast({ message: 'Failed to load comments', type: 'error' });
    } finally {
      setCommentsLoading(false);
    }
  }

  function toggleComments() {
    const opening = !commentsOpen;
    setCommentsOpen(opening);
    if (opening && !commentsLoaded) loadComments();
    if (opening) setTimeout(() => inputRef.current?.focus(), 100);
  }

  async function handleAddComment(e: React.FormEvent) {
    e.preventDefault();
    if (!token) { addToast({ message: 'Sign in to comment', type: 'info' }); return; }
    const trimmed = newComment.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.feed.addComment(post.id, trimmed, token);
      setComments((prev) => [...prev, res.comment]);
      const nc = commentCount + 1;
      setCommentCount(nc);
      onCommentAdded?.(post.id, nc);
      setNewComment('');
    } catch {
      addToast({ message: 'Failed to post comment', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const isOwner = user?.id === post.user.id;
  const color = avatarColor(post.user.id);
  const emoji = avatarEmoji(post.user.id, post.user.gender);
  const mood = post.mood ? MOOD_CONFIG[post.mood] : null;

  async function handleLike() {
    if (!token) { addToast({ message: 'Sign in to like posts', type: 'info' }); return; }
    if (liking) return;
    setLiking(true);
    try {
      const res = await api.feed.toggleLike(post.id, token);
      onLikeChange?.(post.id, res.likedByMe, res.likeCount);
    } catch { addToast({ message: 'Failed to like', type: 'error' }); }
    finally { setLiking(false); }
  }

  async function handleDelete() {
    if (!token || deleting) return;
    setDeleting(true);
    try {
      await api.feed.deletePost(post.id, token);
      onDelete?.(post.id);
    } catch { addToast({ message: 'Failed to delete', type: 'error' }); setDeleting(false); }
  }

  const cardBase = post.type === 'CONFESSION' && mood
    ? `${mood.bg} ${mood.border}`
    : 'bg-brand-surface/60 border-white/[0.06]';

  return (
    <article
      className={`border rounded-2xl overflow-hidden ${cardBase}`}
      style={{
        transition:
          'border-color 220ms cubic-bezier(0.23, 1, 0.32, 1), transform 220ms cubic-bezier(0.23, 1, 0.32, 1)',
      }}
    >
      {/* Photo */}
      {post.type === 'PHOTO' && post.mediaData && (
        <div
          className="relative cursor-pointer overflow-hidden bg-brand-black"
          onClick={() => setImgExpanded(!imgExpanded)}
        >
          <img
            src={post.mediaData}
            alt="Post image"
            className={`w-full object-cover ${imgExpanded ? 'max-h-[80vh]' : 'max-h-[420px]'}`}
            style={{ transition: 'max-height 280ms cubic-bezier(0.23, 1, 0.32, 1)' }}
          />
        </div>
      )}

      {/* Video — local upload (base64) */}
      {post.type === 'VIDEO' && post.mediaData && post.mediaType?.startsWith('video/') && (
        <video
          src={post.mediaData}
          controls
          muted
          playsInline
          className="w-full max-h-[420px] bg-black object-contain"
        />
      )}

      {/* Video — URL (YouTube embed or direct link) */}
      {post.type === 'VIDEO' && post.videoUrl && (() => {
        const ytId = getYouTubeId(post.videoUrl);
        return ytId ? (
          <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${ytId}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <video
            src={post.videoUrl}
            controls
            muted
            playsInline
            className="w-full max-h-[420px] bg-black object-contain"
          />
        );
      })()}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-full border flex items-center justify-center text-base shrink-0 ${color}`}>
              {emoji}
            </div>
            <div>
              <p className="text-xs font-semibold text-brand-white/60">Anonymous</p>
              <p className="text-[11px] text-brand-gray">{timeAgo(post.createdAt)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Confession mood tag */}
            {post.type === 'CONFESSION' && mood && (
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${mood.bg} ${mood.border} ${mood.text}`}>
                {mood.emoji} {mood.label}
              </span>
            )}
            {/* Video type badge */}
            {post.type === 'VIDEO' && (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">
                <Play size={10} fill="currentColor" /> Video
              </span>
            )}
            {isOwner && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="p-1 text-brand-gray/50 hover:text-brand-red rounded-lg hover:bg-brand-red/10 active:scale-[0.92]"
                style={{
                  transition:
                    'color 180ms cubic-bezier(0.23, 1, 0.32, 1), background-color 180ms cubic-bezier(0.23, 1, 0.32, 1), transform 120ms ease-out',
                }}
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Content / Caption */}
        {post.content && (
          <p className={`text-sm leading-relaxed whitespace-pre-wrap break-words mb-3 ${
            post.type === 'CONFESSION' ? 'text-brand-white text-base font-medium italic' : 'text-brand-white/90'
          }`}>
            {post.type === 'CONFESSION' && <span className="not-italic mr-1">💬</span>}
            {post.content}
          </p>
        )}

        {/* Like + Comment */}
        <div className="flex items-center gap-1 mt-1">
          <button
            onClick={handleLike}
            disabled={liking}
            className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded-lg active:scale-[0.94] ${
              post.likedByMe ? 'text-brand-red' : 'text-brand-gray hover:text-brand-red hover:bg-brand-red/5'
            }`}
            style={{
              transition:
                'color 180ms cubic-bezier(0.23, 1, 0.32, 1), background-color 180ms cubic-bezier(0.23, 1, 0.32, 1), transform 120ms ease-out',
            }}
          >
            <Heart
              size={15}
              className={`${post.likedByMe ? 'fill-brand-red' : ''} ${liking ? 'animate-heart-pop' : ''}`}
              style={{ transition: 'fill 180ms cubic-bezier(0.23, 1, 0.32, 1)' }}
            />
            <span className="font-medium tabular-nums">{post.likeCount}</span>
          </button>

          <button
            onClick={toggleComments}
            className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded-lg active:scale-[0.94] ${
              commentsOpen ? 'text-brand-lime' : 'text-brand-gray hover:text-brand-lime hover:bg-brand-lime/5'
            }`}
            style={{ transition: 'color 180ms cubic-bezier(0.23, 1, 0.32, 1), background-color 180ms cubic-bezier(0.23, 1, 0.32, 1), transform 120ms ease-out' }}
          >
            <MessageCircle size={15} />
            <span className="font-medium tabular-nums">{commentCount}</span>
          </button>
        </div>
      </div>

      {/* Comments section */}
      {commentsOpen && (
        <div className="border-t border-white/[0.06] px-4 pb-4 pt-3 flex flex-col gap-3">
          {commentsLoading && comments.length === 0 && (
            <div className="flex gap-2 py-2">
              {[1,2].map((i) => (
                <div key={i} className="h-3 bg-brand-surface-2 rounded animate-pulse w-1/3" />
              ))}
            </div>
          )}

          {comments.map((c) => (
            <div key={c.id} className="flex gap-2.5 items-start">
              <div className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs shrink-0 ${avatarColor(c.user.id)}`}>
                {avatarEmoji(c.user.id, c.user.gender)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-brand-gray mb-0.5">Anonymous · {timeAgo(c.createdAt)}</p>
                <p className="text-sm text-brand-white/90 break-words">{c.content}</p>
              </div>
            </div>
          ))}

          {hasMore && (
            <button
              onClick={() => loadComments(nextCursor || undefined)}
              disabled={commentsLoading}
              className="text-xs text-brand-gray hover:text-brand-lime flex items-center gap-1"
            >
              <ChevronDown size={12} /> Load more
            </button>
          )}

          <form onSubmit={handleAddComment} className="flex gap-2 mt-1">
            <input
              ref={inputRef}
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value.slice(0, 300))}
              placeholder="Add a comment…"
              maxLength={300}
              className="flex-1 bg-brand-surface-2 border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-brand-white placeholder:text-brand-gray/50 outline-none focus:border-brand-lime/40"
            />
            <button
              type="submit"
              disabled={!newComment.trim() || submitting}
              className="p-2 rounded-xl bg-brand-lime/10 text-brand-lime hover:bg-brand-lime/20 disabled:opacity-40 active:scale-[0.94]"
              style={{ transition: 'background-color 160ms ease, transform 100ms ease' }}
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      )}
    </article>
  );
}
